import React from 'react'
import { observer } from 'mobx-react'

import Button from 'components/Button'
import ErrorBlock from 'components/ErrorBlock'
import Icon from 'components/Icon'

import { Form, FormRow, TextInputContainer } from 'components/inputs'
import PasswordResetViewModel from './PasswordResetViewModel'
import styles from './styles.sass'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  vm: PasswordResetViewModel
}

export default observer(function PasswordReset({ vm }: Props) {
  return (
    <div className={styles.root}>
      <div className={styles.header}></div>
      <div className={styles.content}>
        <div className={styles.title}>You have been invited!</div>
        <div className={styles.subtitle}>
          Please set a secure password for signing in and accessing your data.
        </div>
        <div className={styles.form}>
          <Form onSubmit={vm.resetPassword}>
            <FormRow>
              <TextInputContainer
                model={vm}
                field="email"
                name="email"
                label="Email"
                disabled
              />
            </FormRow>

            <FormRow>
              <TextInputContainer
                model={vm}
                field="password"
                name="password"
                label="Password"
                type="password"
              />
            </FormRow>

            <FormRow>
              <div className={styles.requirements}>
                <List>
                  <ListItem
                    title="One lowercase character"
                    valid={vm.containsLowerCase}
                  />
                  <ListItem
                    title="One uppercase character"
                    valid={vm.containsUpperCase}
                  />
                  <ListItem title="One number" valid={vm.containsOneNumber} />
                </List>

                <List>
                  <ListItem
                    title="One special character"
                    valid={vm.containsSpecialCharacters}
                  />
                  <ListItem
                    title="8 character minimum"
                    valid={vm.validMinimumLength}
                  />
                  <ListItem
                    title="50 characters maximum"
                    valid={vm.validMaximumLength}
                  />
                </List>
              </div>
            </FormRow>

            <FormRow className={styles.submit}>
              {vm.resetPassword.rejected && (
                <ErrorBlock error={vm.resetPassword.error} />
              )}

              <Button
                submit
                type="primary"
                size="wide"
                className={styles.submit}
                disabled={!vm.valid}
                loading={vm.resetPassword.pending}
              >
                Reset Password
              </Button>
            </FormRow>
          </Form>
        </div>
      </div>
      <div className={styles.footer}>
        <div>Copyright &copy; Tickmark, Inc {new Date().getFullYear()}</div>
      </div>
    </div>
  )
})

interface ListItemProps {
  title: string
  valid: boolean
}

function ListItem({ title, valid }: ListItemProps) {
  return (
    <li>
      <Icon
        className={styles.icon}
        size={16}
        name={valid ? 'checkbox-invert' : 'close'}
      />{' '}
      {title}
    </li>
  )
}

interface ListProps extends React.HTMLAttributes<HTMLUListElement> {}

function List({ children }: ListProps) {
  return <ul className={styles.list}>{children}</ul>
}
